import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  MenuItem,
  CircularProgress,
  Typography,
  Rating,
  Autocomplete
} from '@mui/material'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { apiUrl, API_ENDPOINTS } from '../Services/api'
import { useAuth } from '../hooks/useAuth'
import { useSnackbar } from 'notistack'

const BookFormDialog = ({ open, onClose, book, type, onSuccess }) => {
  const { token } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const isEdit = Boolean(book)
  const isMyBooks = type === 'myBooks'

  const [loading, setLoading] = useState(false)
  const [imgError, setImgError] = useState(false)

  const [form, setForm] = useState({})
  const [availableGenres, setAvailableGenres] = useState([])
  const [availableSeries, setAvailableSeries] = useState([])
  const [alternativeEditions, setAlternativeEditions] = useState([])

  const [searchIsbn, setSearchIsbn] = useState('')
  const [isSearching, setIsSearching] = useState(false)

  const handleSearchIsbn = async () => {
    const cleanIsbn = searchIsbn.trim().replace(/-/g, '')
    if (!cleanIsbn) {
      enqueueSnackbar('Veuillez entrer un ISBN.', { variant: 'warning' })
      return
    }

    setIsSearching(true)
    setAlternativeEditions([])
    try {
      const fetchUrl = apiUrl(`/books/get/search-external-isbn/${cleanIsbn}`)
      const response = await fetch(fetchUrl, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      })
      if (response.status === 204) {
        throw new Error('Aucun livre trouvé pour cet ISBN.')
      }
      if (!response.ok) {
        throw new Error('Erreur lors de la recherche du livre.')
      }
      const data = await response.json()

      let publish_date = data.publish_date || ''
      if (publish_date) {
        const parsedDate = dayjs(publish_date)
        if (parsedDate.isValid()) {
          publish_date = parsedDate.format('YYYY-MM-DD')
        } else {
          publish_date = ''
        }
      }

      setForm(s => ({
        ...s,
        title: data.title || s.title,
        author: data.author || s.author,
        publisher: data.publisher || s.publisher,
        pages: data.pages || s.pages,
        publish_date: publish_date || s.publish_date,
        summary: data.summary || s.summary,
        image_url: data.image_url || '',
        ...(cleanIsbn.length === 10 ? { isbn_10: cleanIsbn } : {}),
        ...(cleanIsbn.length === 13 ? { isbn_13: cleanIsbn } : {})
      }))

      if (data.alternative_editions && data.alternative_editions.length > 0) {
        setAlternativeEditions(data.alternative_editions)
        enqueueSnackbar('Livre trouvé. Des couvertures alternatives sont disponibles !', { variant: 'info' })
      } else {
        enqueueSnackbar('Livre trouvé et formulaire pré-rempli !', { variant: 'success' })
      }
      setImgError(false)
    } catch (err) {
      enqueueSnackbar(err.message || 'Erreur lors de la recherche.', { variant: 'error' })
    } finally {
      setIsSearching(false)
    }
  }

  useEffect(() => {
    if (open) {
      setImgError(false)
      setSearchIsbn('')
      setAlternativeEditions([])
      if (book) {
        setForm({
          title: book.title || '',
          author: book.author || '',
          summary: book.summary || '',
          publisher: book.publisher || '',
          isbn_10: book.isbn_10 || '',
          isbn_13: book.isbn_13 || '',
          genre: book.genre || '',
          pages: book.pages || '',
          publish_date: book.publish_date || '',
          image_url: book.image_url || '',
          priority: book.priority || 3,
          note_5: book.note_5 || '',
          personnal_note: book.personnal_note || '',
          started_date: book.started_date || '',
          ended_date: book.ended_date || '',
          series_name: book.series?.name || '',
          volume_number: book.volume_number || '',
        })
      } else {
        setForm({
          title: '',
          author: '',
          summary: '',
          publisher: '',
          isbn_10: '',
          isbn_13: '',
          genre: '',
          pages: '',
          publish_date: '',
          image_url: '',
          priority: 3,
          note_5: '',
          personnal_note: '',
          started_date: '',
          ended_date: '',
          series_name: '',
          volume_number: '',
        })
      }
      
      // Fetch dynamic genres
      fetch(apiUrl(API_ENDPOINTS.BOOKS.GENRES), {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
      })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setAvailableGenres(data.map(g => g.name))
        }
      })
      .catch(err => console.error('Erreur lors de la récupération des genres:', err))

      // Fetch dynamic series
      fetch(apiUrl('/books/get/series'), {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
      })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setAvailableSeries(data.map(s => s.name))
        }
      })
      .catch(err => console.error('Erreur lors de la récupération des séries:', err))

    }
  }, [open, book])

  const handleChange = (key) => (e) => {
    const value = e?.target?.value
    setForm((s) => ({ ...s, [key]: value }))
  }

  const validateForm = () => {
    if (!form.title) {
      enqueueSnackbar('Le titre est requis.', { variant: 'error' })
      return false
    }

    // Basic ISBN validations
    if (form.isbn_10 && form.isbn_10.length !== 10) {
      enqueueSnackbar("L'ISBN-10 doit contenir exactement 10 caractères.", { variant: 'error' })
      return false
    }
    if (form.isbn_13 && form.isbn_13.length !== 13) {
      enqueueSnackbar("L'ISBN-13 doit contenir exactement 13 caractères.", { variant: 'error' })
      return false
    }

    // Date validations
    if (isMyBooks && form.started_date && form.ended_date) {
      if (dayjs(form.ended_date).isBefore(dayjs(form.started_date))) {
        enqueueSnackbar("La date de fin ne peut pas être avant la date de début.", { variant: 'error' })
        return false
      }
    }

    return true
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    setLoading(true)
    try {
      let endpoint = ''
      if (isMyBooks) {
        endpoint = isEdit ? `/books/edit/${book.id}` : '/books/create'
      } else {
        endpoint = isEdit ? `/reading-list/edit/${book.id}` : '/reading-list/create'
      }

      const url = apiUrl(endpoint)
      const body = {
        title: String(form.title || ''),
        summary: String(form.summary || ''),
        author: String(form.author || ''),
        isbn_10: String(form.isbn_10 || ''),
        isbn_13: String(form.isbn_13 || ''),
        genre: String(form.genre || ''),
        publisher: String(form.publisher || ''),
        pages: form.pages ? Number(form.pages) : undefined,
        image_url: String(form.image_url || ''),
        series_name: String(form.series_name || '').trim(),
        volume_number: form.volume_number ? Number(form.volume_number) : undefined,
      }

      if (form.publish_date) body.publish_date = String(form.publish_date)

      if (isMyBooks) {
        if (form.note_5) body.note_5 = Number(form.note_5)
        body.personnal_note = String(form.personnal_note || '')
        if (form.started_date) body.started_date = String(form.started_date)
        if (form.ended_date) body.ended_date = String(form.ended_date)
      } else {
        body.priority = form.priority ? Number(form.priority) : 0
      }

      const response = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || 'Erreur lors de la sauvegarde.')
      }

      enqueueSnackbar(isEdit ? 'Livre modifié avec succès.' : 'Livre ajouté avec succès.', { variant: 'success' })
      onSuccess()
      onClose()
    } catch (err) {
      enqueueSnackbar(err?.message || 'Erreur lors de la sauvegarde.', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {isEdit ? 'Modifier le livre' : 'Ajouter un livre'}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          {/* Image preview */}
          <Box sx={{ width: { xs: '100%', md: '35%' }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', gap: 2 }}>
            <Box
              component="img"
              src={!imgError && form.image_url ? form.image_url : require('../Assets/coming-soon.svg').default}
              alt="Aperçu"
              onError={() => setImgError(true)}
              sx={{
                width: '100%',
                borderRadius: 2,
                objectFit: 'cover',
                minHeight: { xs: 200, md: 200 },
                boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
              }}
            />
            {alternativeEditions.length > 0 && (
              <Box sx={{ width: '100%', mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>Couvertures alternatives</Typography>
                <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', p: 1, '&::-webkit-scrollbar': { height: 6 }, '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,.2)', borderRadius: 10 } }}>
                  {alternativeEditions.map((edition, idx) => (
                    <Box
                      key={idx}
                      component="img"
                      src={edition.image_url}
                      alt={`Alternative ${idx}`}
                      onClick={() => setForm(s => ({ ...s, image_url: edition.image_url }))}
                      sx={{
                        height: 80,
                        cursor: 'pointer',
                        borderRadius: 1,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'scale(1.05)' }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
              <Typography component="legend" color="text.secondary" sx={{ mb: 1 }}>Genre</Typography>
              <TextField
                select
                size="small"
                fullWidth
                value={form.genre || ''}
                onChange={handleChange('genre')}
              >
                {availableGenres.map((g) => (
                  <MenuItem key={g} value={g}>{g}</MenuItem>
                ))}
              </TextField>
            </Box>
            {isMyBooks && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                <Typography component="legend" color="text.secondary" sx={{ mb: 1 }}>Note</Typography>
                <Rating
                  name="book-rating"
                  value={Number(form.note_5) || 0}
                  onChange={(event, newValue) => {
                    setForm(s => ({ ...s, note_5: newValue }));
                  }}
                  icon={<FavoriteIcon fontSize="inherit" sx={{ color: '#F6658C' }} />}
                  emptyIcon={<FavoriteBorderIcon fontSize="inherit" />}
                  size="large"
                />
              </Box>
            )}
            {!isMyBooks && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                <Typography component="legend" color="text.secondary" sx={{ mb: 1 }}>Envie</Typography>
                <Rating
                  name="book-priority"
                  value={Number(form.priority) || 0}
                  onChange={(event, newValue) => {
                    setForm(s => ({ ...s, priority: newValue }));
                  }}
                  icon={<span style={{ fontSize: 'inherit' }}>🔥</span>}
                  emptyIcon={<span style={{ fontSize: 'inherit', opacity: 0.3, filter: 'grayscale(1)' }}>🔥</span>}
                  size="large"
                />
              </Box>
            )}
          </Box>
          {/* Form fields */}
          <Box sx={{ width: { xs: '100%', md: '65%' }, display: 'flex', flexDirection: 'column', gap: 1 }}>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
              <TextField
                size="small"
                label="Auto-remplissage via ISBN"
                placeholder="Ex: 9782070415731"
                value={searchIsbn}
                onChange={(e) => setSearchIsbn(e.target.value)}
                fullWidth
              />
              <Button
                variant="outlined"
                onClick={handleSearchIsbn}
                disabled={isSearching || !searchIsbn.trim()}
                sx={{ minWidth: '120px', height: '40px' }}
              >
                {isSearching ? <CircularProgress size={20} /> : 'Rechercher'}
              </Button>
            </Box>

            <TextField
              autoFocus
              margin="dense"
              label="Titre"
              fullWidth
              required
              value={form.title}
              onChange={handleChange('title')}
              InputLabelProps={{ shrink: form.title ? true : undefined }}
            />
            <TextField
              margin="dense"
              label="Auteur"
              fullWidth
              value={form.author}
              onChange={handleChange('author')}
              InputLabelProps={{ shrink: form.author ? true : undefined }}
            />
            <TextField
              margin="dense"
              label="Éditeur"
              fullWidth
              value={form.publisher}
              onChange={handleChange('publisher')}
              InputLabelProps={{ shrink: form.publisher ? true : undefined }}
            />
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <Autocomplete
                freeSolo
                options={availableSeries}
                value={form.series_name || ''}
                onChange={(e, newValue) => setForm(s => ({ ...s, series_name: newValue || '' }))}
                onInputChange={(e, newInputValue) => setForm(s => ({ ...s, series_name: newInputValue || '' }))}
                sx={{ flexGrow: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="dense"
                    label="Série"
                    fullWidth
                    InputLabelProps={{ shrink: form.series_name ? true : undefined }}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'off',
                    }}
                  />
                )}
              />
              <TextField
                margin="dense"
                label="Tome"
                type="number"
                inputProps={{ step: 0.1 }}
                sx={{ width: { xs: '100%', md: '120px' } }}
                value={form.volume_number || ''}
                onChange={handleChange('volume_number')}
                InputLabelProps={{ shrink: form.volume_number ? true : undefined }}
              />
            </Box>
            <DatePicker
              label="Date de publication"
              value={form.publish_date ? dayjs(form.publish_date) : null}
              onChange={(val) => setForm((s) => ({ ...s, publish_date: val ? val.format('YYYY-MM-DD') : '' }))}
              slotProps={{ textField: { margin: 'dense', fullWidth: true }, field: { clearable: true } }}
            />
            <TextField
              margin="dense"
              label="Pages"
              type="number"
              fullWidth
              value={form.pages}
              onChange={handleChange('pages')}
              InputLabelProps={{ shrink: form.pages ? true : undefined }}
            />
            <TextField
              margin="dense"
              label="Résumé"
              fullWidth
              multiline
              rows={3}
              value={form.summary}
              onChange={handleChange('summary')}
              InputLabelProps={{ shrink: form.summary ? true : undefined }}
            />

            {isMyBooks && (
              <>
                <TextField
                  margin="dense"
                  label="Note personnelle"
                  fullWidth
                  multiline
                  rows={2}
                  value={form.personnal_note}
                  onChange={handleChange('personnal_note')}
                  InputLabelProps={{ shrink: form.personnal_note ? true : undefined }}
                />
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1 }}>
                  <DatePicker
                    label="Date de début"
                    value={form.started_date ? dayjs(form.started_date) : null}
                    onChange={(val) => setForm((s) => ({ ...s, started_date: val ? val.format('YYYY-MM-DD') : '' }))}
                    slotProps={{ textField: { margin: 'dense', fullWidth: true }, field: { clearable: true } }}
                  />
                  <DatePicker
                    label="Date de fin"
                    value={form.ended_date ? dayjs(form.ended_date) : null}
                    onChange={(val) => setForm((s) => ({ ...s, ended_date: val ? val.format('YYYY-MM-DD') : '' }))}
                    slotProps={{ textField: { margin: 'dense', fullWidth: true }, field: { clearable: true } }}
                  />
                </Box>
              </>
            )}

            <TextField
              margin="dense"
              label="Image URL"
              fullWidth
              value={form.image_url}
              onChange={(e) => {
                setImgError(false)
                handleChange('image_url')(e)
              }}
              InputLabelProps={{ shrink: form.image_url ? true : undefined }}
            />
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <TextField
                margin="dense"
                label="ISBN-10"
                fullWidth
                value={form.isbn_10}
                onChange={handleChange('isbn_10')}
                InputLabelProps={{ shrink: form.isbn_10 ? true : undefined }}
              />
              <TextField
                margin="dense"
                label="ISBN-13"
                fullWidth
                value={form.isbn_13}
                onChange={handleChange('isbn_13')}
                InputLabelProps={{ shrink: form.isbn_13 ? true : undefined }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Annuler</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : (isEdit ? 'Modifier' : 'Ajouter')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default BookFormDialog
